import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy, useEffect, useState } from 'react'
import { AppShell } from './components/layout'
import { useAuthStore } from './state/auth'
import { useProfileStore } from './state/profile'
import { LoadingSpinner } from './components/common/LoadingSpinner'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { LoginPage } from './pages/LoginPage'
import { debugLog } from './utils/debug-log'

// Lazy-load the autonomous dashboard surfaces for code splitting.
const MissionControlPage = lazy(() =>
  import('./pages/MissionControlPage').then((m) => ({ default: m.MissionControlPage })),
)
const SetupPage = lazy(() => import('./pages/SetupPage').then((m) => ({ default: m.SetupPage })))
const PerformancePage = lazy(() =>
  import('./pages/PerformancePage').then((m) => ({ default: m.PerformancePage })),
)
const PositionsPage = lazy(() =>
  import('./pages/PositionsPage').then((m) => ({ default: m.PositionsPage })),
)
const DiscoveryPage = lazy(() =>
  import('./pages/DiscoveryPage').then((m) => ({ default: m.DiscoveryPage })),
)
const ActivityPage = lazy(() =>
  import('./pages/ActivityPage').then((m) => ({ default: m.ActivityPage })),
)
const ExecutionPage = lazy(() =>
  import('./pages/ExecutionPage').then((m) => ({ default: m.ExecutionPage })),
)
const RiskPage = lazy(() => import('./pages/RiskPage').then((m) => ({ default: m.RiskPage })))
const AlertsPage = lazy(() => import('./pages/AlertsPage').then((m) => ({ default: m.AlertsPage })))
const SystemPage = lazy(() => import('./pages/SystemPage').then((m) => ({ default: m.SystemPage })))

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

/** Redirects to /setup when no completed profile exists. Only wraps AppShell routes. */
function SetupGate({ children }: { children: React.ReactNode }) {
  const setupComplete = useProfileStore((s) => s.setupComplete)
  const checkSetupStatus = useProfileStore((s) => s.checkSetupStatus)
  // If the store already knows setup is complete (e.g. just finished wizard),
  // skip the API check entirely to avoid race conditions.
  const [checked, setChecked] = useState(setupComplete)

  useEffect(() => {
    if (!checked) {
      debugLog.info('setup-gate', 'checking setup status', { setupComplete })
      checkSetupStatus().then((result) => {
        debugLog.info('setup-gate', 'check complete', {
          result,
          setupComplete: useProfileStore.getState().setupComplete,
        })
        setChecked(true)
      })
    }
  }, [checked, checkSetupStatus, setupComplete])

  if (!checked) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" label="Checking setup..." />
      </div>
    )
  }
  if (!setupComplete) {
    debugLog.warn('setup-gate', 'redirecting to /setup — setupComplete is false')
    return <Navigate to="/setup" replace />
  }
  return <>{children}</>
}

function PageFallback() {
  return (
    <div className="flex h-64 items-center justify-center">
      <LoadingSpinner size="lg" label="Loading page..." />
    </div>
  )
}

function App() {
  const initialize = useAuthStore((s) => s.initialize)

  useEffect(() => {
    initialize()
  }, [initialize])

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Setup wizard — full-screen, outside AppShell, but requires auth */}
        <Route
          path="/setup"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <SetupPage />
                </Suspense>
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <SetupGate>
                <AppShell />
              </SetupGate>
            </ProtectedRoute>
          }
        >
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <MissionControlPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/performance"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <PerformancePage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/positions"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <PositionsPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/discovery"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <DiscoveryPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/execution"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <ExecutionPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/activity"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <ActivityPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/risk"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <RiskPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/alerts"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <AlertsPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/system"
            element={
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <SystemPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route path="/symbol/:symbol" element={<Navigate to="/discovery" replace />} />
          <Route path="/beliefs" element={<Navigate to="/" replace />} />
          <Route path="/logs" element={<Navigate to="/activity" replace />} />
          <Route path="/promote-to-live" element={<Navigate to="/system" replace />} />
          <Route path="/intelligence" element={<Navigate to="/system" replace />} />
          <Route path="/orders" element={<Navigate to="/execution" replace />} />
          <Route path="/trade" element={<Navigate to="/discovery" replace />} />
          <Route path="/strategies" element={<Navigate to="/discovery" replace />} />
          <Route path="/llm-review" element={<Navigate to="/" replace />} />
          <Route path="/config" element={<Navigate to="/system" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
